import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AppIcon } from '../ui/icon'
import { mdiViewGridOutline, mdiFire, mdiWaterOutline, mdiWindowShutter, mdiAlert, mdiCheck, mdiClose, mdiPencil } from '@mdi/js'
import { Button } from '../ui/button'
import { SectionHeader, EmptyOK, SourceChip, ExceptionDot } from './primitives'
import { useLots, LAYERS, type Lot } from './useLots'

const ANNEX_TYPES = new Set(['cave', 'parking'])
const LAYER_ICON: Record<string, string> = { heat: mdiFire, ecs: mdiWaterOutline, volet: mdiWindowShutter }

function typeLabel(t: string): string {
  return ({ resid: 'Logement', commerce: 'Commerce', loge: 'Loge gardien', parking: 'Parking', cave: 'Cave' } as Record<string, string>)[t] || t
}

export function LotsSection({ org, buildingUid }: { org: string; buildingUid: string }) {
  const { t } = useTranslation('data-reliability')
  const { floors, lots, isLoading, exceptionsFor, confirmLots, applyLayer } = useLots(org, buildingUid)
  const [mode, setMode] = useState<string>('structure')
  const [selUid, setSelUid] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const layer = LAYERS.find((l) => l.id === mode)
  const annexes = useMemo(() => lots.filter((l) => ANNEX_TYPES.has(l.type)), [lots])
  const mainLots = useMemo(() => lots.filter((l) => !ANNEX_TYPES.has(l.type)), [lots])
  const residCount = useMemo(() => lots.filter((l) => l.type === 'resid').length, [lots])
  const exceptions = useMemo(() => mainLots.filter((l) => exceptionsFor(l, mode).length > 0), [mainLots, exceptionsFor, mode])

  const run = async (fn: () => Promise<void>) => {
    setBusy(true)
    try { await fn() } finally { setBusy(false) }
  }
  const pendingNonExc = (pool: Lot[]) => pool.filter((l) => l.pending && exceptionsFor(l, 'structure').length === 0).map((l) => l.uid)

  const confirmAll = () => run(() => confirmLots(pendingNonExc(mainLots)))
  const confirmFloor = (floorUid: string) => run(() => confirmLots(pendingNonExc(mainLots.filter((l) => l.floorUid === floorUid))))
  const confirmAnnexes = () => run(() => confirmLots(annexes.filter((l) => l.pending).map((l) => l.uid)))

  if (isLoading) {
    return (
      <div data-testid="dr-section-lots">
        <SectionHeader title={t('lots.title')} desc={t('lots.desc', { count: 0 })} />
        <div className="h-40 animate-pulse rounded-xl border border-border bg-muted/30" />
      </div>
    )
  }
  if (lots.length === 0) {
    return (
      <div data-testid="dr-section-lots">
        <SectionHeader title={t('lots.title')} desc={t('lots.desc', { count: 0 })} />
        <EmptyOK label={t('lots.empty')} />
      </div>
    )
  }

  const cellTone = (l: Lot): string => {
    const exc = exceptionsFor(l, mode).length > 0
    if (mode === 'structure') {
      if (exc) return 'border-warning bg-warning/15 text-warning'
      if (!l.pending) return 'border-success/40 bg-success/10 text-success'
      return 'border-border bg-muted/40 text-foreground'
    }
    if (l.type !== 'resid') return 'border-transparent bg-transparent text-muted-foreground/50'
    if (exc) return 'border-warning bg-warning/15 text-warning'
    if (layer && l.layers[layer.code] === layer.common) return 'border-success/40 bg-success/10 text-success'
    return 'border-border bg-muted/40 text-muted-foreground'
  }
  const cellAttr = (l: Lot): string => {
    if (mode === 'structure') return l.type === 'resid' ? (l.surface != null ? `${l.surface} m²` : '— m²') : typeLabel(l.type)
    if (l.type !== 'resid') return '—'
    return layer ? layer.show(l.layers[layer.code]) : '?'
  }

  const selected = selUid ? lots.find((l) => l.uid === selUid) : null

  return (
    <div data-testid="dr-section-lots">
      <SectionHeader title={t('lots.title')} desc={t('lots.desc', { count: lots.length })} />

      {/* mode toggle */}
      <div className="mb-4 flex w-fit flex-wrap items-center gap-1 rounded-lg bg-muted/50 p-1">
        <ModeBtn active={mode === 'structure'} onClick={() => { setMode('structure'); setSelUid(null) }} icon={mdiViewGridOutline} label={t('lots.mode_structure')} testid="dr-mode-structure" />
        <div className="mx-1 h-5 w-px bg-border" />
        {LAYERS.map((l) => (
          <ModeBtn key={l.id} active={mode === l.id} onClick={() => { setMode(l.id); setSelUid(null) }} icon={LAYER_ICON[l.id] || mdiViewGridOutline} label={t(l.labelKey)} testid={`dr-mode-${l.id}`} />
        ))}
      </div>

      {/* bulk bar */}
      <div className="mb-3.5 flex flex-wrap items-center gap-3 rounded-xl border border-border bg-card px-4 py-3">
        {exceptions.length > 0 ? (
          <span className="flex flex-1 items-center gap-2 text-sm text-warning">
            <AppIcon path={mdiAlert} size="sm" />
            {t('lots.exceptions', { count: exceptions.length })}
            <span className="text-muted-foreground">{t('lots.exceptions_rest_follows')}</span>
          </span>
        ) : (
          <span className="flex flex-1 items-center gap-2 text-sm text-success">
            <AppIcon path={mdiCheck} size="sm" /> {t('lots.no_exceptions')}
          </span>
        )}
        {mode === 'structure' ? (
          <Button size="sm" onClick={confirmAll} disabled={busy} data-testid="dr-lots-confirm-all">{t('lots.confirm_all')}</Button>
        ) : (
          <Button size="sm" onClick={() => run(() => applyLayer(mode))} disabled={busy} data-testid={`dr-lots-apply-${mode}`}>
            {t('lots.apply_layer', { label: layer ? t(layer.labelKey) : '', count: residCount })}
          </Button>
        )}
      </div>

      {/* floor stack */}
      <div className="rounded-xl border border-border bg-card px-3.5 pb-4 pt-2">
        {floors.filter((f) => mainLots.some((l) => l.floorUid === f.uid)).map((f) => {
          const fl = mainLots.filter((l) => l.floorUid === f.uid)
          const allConf = fl.every((l) => !l.pending)
          return (
            <div key={f.uid} className="flex items-center gap-3.5 border-b border-border/50 py-2.5 last:border-b-0">
              <div className="flex w-14 flex-shrink-0 flex-col gap-1">
                <span className="text-xs font-bold text-foreground">{f.label}</span>
                {mode === 'structure' && (
                  <button onClick={() => confirmFloor(f.uid)} disabled={busy || allConf} className={`text-left text-[11px] ${allConf ? 'text-success' : 'text-primary'}`} data-testid={`dr-floor-confirm-${f.uid}`}>
                    {allConf ? t('lots.floor_validated') : t('lots.floor_validate')}
                  </button>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                {fl.map((l) => (
                  <button
                    key={l.uid}
                    onClick={() => setSelUid(l.uid === selUid ? null : l.uid)}
                    className={`relative flex h-[50px] min-w-[50px] flex-col items-center justify-center rounded-lg border text-xs font-semibold leading-tight transition-colors ${selUid === l.uid ? 'border-primary bg-primary/10' : cellTone(l)}`}
                    data-testid={`dr-lot-${l.num}`}
                  >
                    {exceptionsFor(l, mode).length > 0 && <ExceptionDot />}
                    <span>{l.num}</span>
                    <span className="text-[9.5px] font-medium opacity-70">{cellAttr(l)}</span>
                  </button>
                ))}
              </div>
            </div>
          )
        })}
        {annexes.length > 0 && (
          <div className="flex items-center gap-3.5 pb-0.5 pt-3">
            <div className="w-14 flex-shrink-0 text-xs font-bold text-foreground">{t('lots.annexes')}</div>
            <div className="flex items-center gap-2.5 text-xs text-muted-foreground">
              {annexes.length} {t('lots.annexes_desc')}
              <Button size="sm" variant="outline" onClick={confirmAnnexes} disabled={busy || annexes.every((l) => !l.pending)} data-testid="dr-annexes-confirm">{t('lots.confirm_all_short')}</Button>
            </div>
          </div>
        )}
      </div>

      {/* legend */}
      <div className="mt-3 flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
        <LegendItem className="border-success/50 bg-success/15" label={t('lots.legend_conform')} />
        <LegendItem className="border-warning bg-warning/20" label={t('lots.legend_exception')} />
        <LegendItem className="border-border bg-muted/50" label={t('lots.legend_todo')} />
      </div>

      {/* lot detail */}
      {selected && (
        <div className="mt-3.5 rounded-xl border border-primary bg-card p-4" data-testid="dr-lot-detail">
          <div className="mb-2.5 flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <span className="text-base font-bold text-foreground">{t('lots.lot', { num: selected.num })}</span>
              <span className="text-xs text-muted-foreground">{floors.find((f) => f.uid === selected.floorUid)?.label} · {typeLabel(selected.type)}</span>
            </div>
            <button onClick={() => setSelUid(null)} className="text-muted-foreground" aria-label="close" data-testid="dr-lot-detail-close"><AppIcon path={mdiClose} size="sm" /></button>
          </div>
          {(() => {
            const exc = [...exceptionsFor(selected, 'structure'), ...LAYERS.flatMap((l) => exceptionsFor(selected, l.id))]
            return exc.length > 0 ? (
              <div className="mb-3 flex items-center gap-2 rounded-lg bg-warning/15 px-3 py-2 text-xs text-warning">
                <AppIcon path={mdiAlert} size="xs" /> {exc.map((e) => e.reason).join(' · ')}
              </div>
            ) : null
          })()}
          <div className="mb-3.5 flex flex-wrap gap-5 text-sm">
            <Field k={t('lots.field_surface')} v={selected.surface != null ? `${selected.surface} m²` : '—'} />
            {LAYERS.map((l) => <Field key={l.id} k={t(l.labelKey)} v={selected.type === 'resid' ? l.show(selected.layers[l.code]) : '—'} />)}
            <div className="ml-auto self-center"><SourceChip org={org} source={selected.source} /></div>
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={() => run(() => confirmLots([selected.uid]))} disabled={busy || !selected.pending} data-testid="dr-lot-confirm">{t('lots.confirm_lot')}</Button>
            <Button size="sm" variant="outline" disabled={busy}><AppIcon path={mdiPencil} size="sm" className="mr-1" />{t('card.correct')}</Button>
          </div>
        </div>
      )}
    </div>
  )
}

function ModeBtn({ active, onClick, icon, label, testid }: { active: boolean; onClick: () => void; icon: string; label: string; testid: string }) {
  return (
    <button onClick={onClick} className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-semibold transition-colors ${active ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`} data-testid={testid}>
      <AppIcon path={icon} size="xs" />{label}
    </button>
  )
}

function Field({ k, v }: { k: string; v: string }) {
  return (
    <div>
      <div className="mb-0.5 text-[11px] text-muted-foreground">{k}</div>
      <div className="font-semibold text-foreground">{v}</div>
    </div>
  )
}

function LegendItem({ className, label }: { className: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={`h-2.5 w-2.5 rounded-sm border ${className}`} />
      {label}
    </span>
  )
}
